import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { Observable, ReplaySubject } from 'rxjs';
import { BillItemPendingModel, PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { enumBillItemProductCode } from '@data-layer/enums/payments';
import { GenericService } from '@services/generic.service';
import { enumBillItemUnit } from '@data-layer/enums/lcl';

@Component({
  selector: 'view-charges',
  templateUrl: './view-charges.component.html',
  styleUrls: ['./view-charges.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewChargesComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public bills: PaymentBillItemPendingModel;
  @Input() public isPayed: boolean;
  @Input() public showCategories: boolean = true;
  @Input() public headerFieldKey: string = 'LABELS.LCL_CHARGES';
  @Input() public totalFieldKey: string = 'LABELS.TOTAL_PRICE';

  public isMobile$: Observable<boolean>;
  public DEFAULT_CURRENCY_CODE: typeof DEFAULT_CURRENCY_CODE = DEFAULT_CURRENCY_CODE;

  public MASTER_GROUPS: Array<{ key: string; items: BillItemPendingModel[] }> = [];
  public SECONDARY_GROUPS: Array<{ key: string; items: BillItemPendingModel[] }> = [];

  public MASTER_BILL_ITEMS: enumBillItemProductCode[] = [
    enumBillItemProductCode.ORIGIN_CBM,
    enumBillItemProductCode.ORIGIN_HBL,
    enumBillItemProductCode.LOADING_FEES,
    enumBillItemProductCode.DESTINATION_CBM,
    enumBillItemProductCode.DESTINATION_HBL,
    enumBillItemProductCode.UNLOADING_FEES,
    enumBillItemProductCode.FREIGHT,
  ];
  public ORIGIN_BILL_ITEMS: { key: string; items: enumBillItemProductCode[] } = {
    key: 'ORIGIN',
    items: [enumBillItemProductCode.ORIGIN_CBM, enumBillItemProductCode.ORIGIN_HBL, enumBillItemProductCode.LOADING_FEES],
  };
  public DESTINATION_BILL_ITEMS: { key: string; items: enumBillItemProductCode[] } = {
    key: 'DESTINATION',
    items: [enumBillItemProductCode.DESTINATION_CBM, enumBillItemProductCode.DESTINATION_HBL, enumBillItemProductCode.UNLOADING_FEES],
  };
  public FREIGHT_BILL_ITEMS: { key: string; items: enumBillItemProductCode[] } = {
    key: 'FREIGHT',
    items: [enumBillItemProductCode.FREIGHT],
  };

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(private genericService: GenericService) {}

  public ngOnInit(): void {
    this.isMobile$ = this.genericService.isLaptop$;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.bills.currentValue) {
      this.buildMasterProductsGroups();
      this.buildSecondaryProductsGroups();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public itemQuantityLabel(unit: enumBillItemUnit): string {
    switch (unit) {
      case enumBillItemUnit.CBM:
        return 'LENGTH_UNIT.CBM';
      case enumBillItemUnit.HBL:
        return 'LABELS.HBL';
      case enumBillItemUnit.TON:
        return 'WEIGHT_UNIT.TON';
      default:
        return '';
    }
  }

  public getSumMasterGroup(group: BillItemPendingModel[]): number {
    return this.getSumGroup(group);
  }

  public getSumAdditionalGroup(group: BillItemPendingModel[]): number {
    return this.getSumGroup(group);
  }

  public getSumGroup(group: BillItemPendingModel[]): number {
    const totalSum: number = (group || []).reduce((acc: number, current: BillItemPendingModel) => {
      return acc + current?.price * current?.quantity;
    }, 0);

    return totalSum + this.getSumDiscountGroup(group);
  }

  public getSumDiscountGroup(group: BillItemPendingModel[]): number {
    const totalDiscounts: number = (group || []).reduce((acc: number, current: BillItemPendingModel) => {
      return acc - current?.discountAmount;
    }, 0);

    return totalDiscounts;
  }

  public totalSecondaryBills(group: BillItemPendingModel[]): number {
    return (group || []).reduce((acc: number, current: BillItemPendingModel) => acc + current?.price * current?.quantity, 0);
  }

  public isShowDescription(productCode: enumBillItemProductCode): boolean {
    return [enumBillItemProductCode.EXPORT_LICENSE, enumBillItemProductCode.SALES_TAX, enumBillItemProductCode.COLLECTION_FEE].includes(
      productCode,
    );
  }

  public discountBillItems(group: BillItemPendingModel[]): BillItemPendingModel[] {
    const groupDiscountGroupIds: string[] = (group || []).map((item: BillItemPendingModel) => item.discountGroupId);

    return (this.discountsBillItems?.items || []).filter((item: BillItemPendingModel) => {
      return groupDiscountGroupIds.includes(item.discountGroupId);
    });
  }

  public discountBillItemsValue(group: BillItemPendingModel[]): number {
    return this.getSumDiscountGroup(group);
  }

  public get totalMasterBills(): number {
    let totalValue: number = 0;
    this.MASTER_GROUPS?.forEach((billGroup: { key: string; items: BillItemPendingModel[] }) => {
      totalValue += (billGroup.items || []).reduce((acc: number, current: BillItemPendingModel) => acc + current.price, 0);
    });
    return totalValue;
  }

  public get totalSum(): number {
    return (
      this.totalMasterBills +
        this.totalSecondaryBills(this.additionalBillItems.items) +
        this.totalSecondaryBills(this.deductionsBillItems.items) +
        this.totalSecondaryBills(this.discountsBillItems.items) || 0
    );
  }

  private buildMasterProductsGroups(): void {
    this.MASTER_GROUPS = [];
    [this.ORIGIN_BILL_ITEMS, this.DESTINATION_BILL_ITEMS, this.FREIGHT_BILL_ITEMS].forEach(
      (billGroup: { key: string; items: enumBillItemProductCode[] }) => {
        const items: BillItemPendingModel[] = this.getMasterGroupBillItems(billGroup.items);
        if (items.length > 0) {
          this.MASTER_GROUPS.push({ key: billGroup.key, items: items });
        }
      },
    );
  }

  private buildSecondaryProductsGroups(): void {
    this.SECONDARY_GROUPS = [];
    [this.additionalBillItems, this.deductionsBillItems, this.collectionFeeBillItems].forEach(
      (billGroup: { key: string; items: BillItemPendingModel[] }) => {
        if ((billGroup?.items || []).length > 0) {
          this.SECONDARY_GROUPS.push(billGroup);
        }
      },
    );
  }

  private getMasterGroupBillItems(items: enumBillItemProductCode[]): BillItemPendingModel[] {
    return this.bills?.items.filter((item: BillItemPendingModel) => items.includes(item.productCode));
  }

  private get additionalBillItems(): { key: string; items: BillItemPendingModel[] } {
    return {
      key: 'ADDITIONAL_CHARGES',
      items: [
        ...(this.bills?.items || []).filter(
          (item: BillItemPendingModel) =>
            !this.MASTER_BILL_ITEMS.includes(item.productCode) &&
            item.price >= 0 &&
            ![enumBillItemProductCode.DISCOUNT, enumBillItemProductCode.COLLECTION_FEE].includes(item.productCode),
        ),
        ...this.collectionFeeBillItems,
      ],
    };
  }

  private get deductionsBillItems(): { key: string; items: BillItemPendingModel[] } {
    return {
      key: 'DEDUCTIONS',
      items: this.bills?.items.filter(
        (item: BillItemPendingModel) =>
          !this.MASTER_BILL_ITEMS.includes(item.productCode) &&
          item.price < 0 &&
          ![enumBillItemProductCode.DISCOUNT, enumBillItemProductCode.COLLECTION_FEE].includes(item.productCode),
      ),
    };
  }

  private get discountsBillItems(): { key: string; items: BillItemPendingModel[] } {
    return {
      key: 'DISCOUNTS',
      items: this.bills?.items.filter(
        (item: BillItemPendingModel) =>
          !this.MASTER_BILL_ITEMS.includes(item.productCode) && item.productCode === enumBillItemProductCode.DISCOUNT,
      ),
    };
  }

  private get collectionFeeBillItems(): BillItemPendingModel[] {
    const collectionFeeItems: BillItemPendingModel[] =
      this.bills?.items.filter(
        (item: BillItemPendingModel) =>
          !this.MASTER_BILL_ITEMS.includes(item.productCode) && item.productCode === enumBillItemProductCode.COLLECTION_FEE,
      ) || [];
    const collectionFeeSum: number =
      (collectionFeeItems || []).reduce((acc: number, curr: BillItemPendingModel) => acc + curr.price, 0) || 0;

    return !!collectionFeeItems.length ? [{ ...collectionFeeItems[0], price: collectionFeeSum }] : [];
  }
}
